import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  UserAgent,
} from "sip.js";
import { paycallApi } from "../api/paycall.api";
import type { AppDispatch } from "../store";
import {
  isPaycallSipWebhookBridgeEnabled,
  paycallDevMockFieldsEnabled,
} from "../../utils/telephony";
import type {
  TCallDirection,
  TSipConnectionStatus,
  TSipCredentials,
  TPaycallWebhookPayload,
} from "./paycall.types";
import {
  buildAnswerWebhook,
  buildEndWebhook,
  buildStartWebhook,
  nowPaycallTimestamp,
  PAYCALL_DEV_MOCK,
  PAYCALL_INBOUND_CALLEE,
} from "./paycall.webhook";

const SIP_INSTANCE_ID_STORAGE_KEY = "paycall.sip.instanceId";

/** How long to wait for the registrar to accept a REGISTER before treating it as failed. */
const SIP_REGISTRATION_TIMEOUT_MS = 15000;

/**
 * Stable RFC 5626 instance-id for this browser, persisted across reloads.
 * Combined with regId, it makes the registrar REPLACE this endpoint's binding
 * on every re-register instead of accumulating stale contacts — the root cause
 * of "503 Service Unavailable / Too many registered contacts" (and of inbound
 * calls forking to a dead tab so answering sends no 200 OK).
 */
function getStableSipInstanceId(): string {
  try {
    const existing = localStorage.getItem(SIP_INSTANCE_ID_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    /* localStorage blocked (private mode / iframe) — fall through to session id */
  }

  const generated = crypto.randomUUID();
  try {
    localStorage.setItem(SIP_INSTANCE_ID_STORAGE_KEY, generated);
  } catch {
    /* persistence unavailable — id stays stable for this page session only */
  }
  return generated;
}

export function createOutgoingCallId(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const random = Math.floor(Math.random() * 1_000_000)
    .toString()
    .padStart(6, "0");

  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}${random}`
  );
}

class PaycallSoftphoneService {
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private currentSession: Session | null = null;
  private creds: TSipCredentials | null = null;
  private remoteAudio: HTMLAudioElement | null = null;

  private sipCallId: string | null = null;
  private sipCaller: string | null = null;
  private callDestination: string | null = null;
  private callAnswered = false;
  private endPosted = false;
  private callStartTimeStr: string | null = null;
  private callAnswerTimeStr: string | null = null;
  private callRingMs: number | null = null;
  private callAnswerMs: number | null = null;
  private callDid: string | null = null;
  private callDirection: TCallDirection = "Inbound";
  private isOutbound = false;

  private status: TSipConnectionStatus = "disconnected";
  /** False once the credentials endpoint 404s — this user has no extension provisioned. */
  private credentialsConfigured = true;
  private statusListeners = new Set<(status: TSipConnectionStatus) => void>();
  private sessionEndedListeners = new Set<() => void>();
  private remoteAnsweredListeners = new Set<(answered: boolean) => void>();
  private connectInFlight: Promise<boolean> | null = null;
  private appDispatch: AppDispatch | null = null;

  bindDispatch(dispatch: AppDispatch): void {
    this.appDispatch = dispatch;
  }

  getMyExtension(): string | null {
    const ext = this.creds?.username?.trim();
    return ext || null;
  }

  getSipStatus(): TSipConnectionStatus {
    return this.status;
  }

  /**
   * Ground truth from the SIP stack: is the registrar holding a binding for us?
   *
   * Prefer this over getSipStatus() for anything that gates call handling. `status` is
   * derived from the connect() promise flow, which can end up "error" while we are in
   * fact registered — e.g. a concurrent REGISTER from the transport's onConnect wins the
   * race and ours rejects, or the 200 OK lands just after our timeout.
   */
  isSipRegistered(): boolean {
    return this.registerer?.state === RegistererState.Registered;
  }

  getCurrentCallId(): string | null {
    return this.sipCallId;
  }

  isOutboundCall(): boolean {
    return this.isOutbound;
  }

  isRemoteAnswered(): boolean {
    return this.callAnswered;
  }

  hasActiveSession(): boolean {
    return this.currentSession != null;
  }

  onStatusChange(cb: (status: TSipConnectionStatus) => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  onSessionEnded(cb: () => void): () => void {
    this.sessionEndedListeners.add(cb);
    return () => this.sessionEndedListeners.delete(cb);
  }

  onRemoteAnsweredChange(cb: (answered: boolean) => void): () => void {
    this.remoteAnsweredListeners.add(cb);
    cb(this.isOutbound && this.callAnswered);
    return () => this.remoteAnsweredListeners.delete(cb);
  }

  setRemoteAudioElement(el: HTMLAudioElement | null): void {
    this.remoteAudio = el;
  }

  async connect(): Promise<boolean> {
    if (this.status === "registered") return true;
    if (this.connectInFlight) return this.connectInFlight;

    this.connectInFlight = this.doConnect();
    try {
      return await this.connectInFlight;
    } finally {
      this.connectInFlight = null;
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (this.registerer) await this.registerer.unregister();
    } catch {
      /* ignore */
    }

    try {
      if (this.userAgent) await this.userAgent.stop();
    } catch {
      /* ignore */
    }

    this.registerer = null;
    this.userAgent = null;
    this.creds = null;
    this.currentSession = null;
    this.resetCallTracking();
    this.setStatus("disconnected");
  }

  async answerSipCall(): Promise<void> {
    const session = this.currentSession;
    if (
      session instanceof Invitation &&
      session.state !== SessionState.Established
    ) {
      await session.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
    }

    this.markCallAnswered();

    if (this.sipCallId && this.isWebhookBridgeEnabled()) {
      const exten = this.extension();
      await this.postWebhook(
        buildAnswerWebhook({
          caller: this.sipCaller ?? "",
          callee:
            this.callDirection === "Inbound"
              ? PAYCALL_INBOUND_CALLEE
              : exten,
          uniqueid: this.sipCallId,
          exten,
          answerTime: this.callAnswerTimeStr!,
          callDirection: this.callDirection,
        })
      );
    }
  }

  async rejectSipCall(): Promise<void> {
    this.postEnd("Not Answer", "Rejected by agent");

    const session = this.currentSession;
    if (session instanceof Invitation) {
      try {
        await session.reject();
      } catch {
        /* ignore */
      }
    }

    this.currentSession = null;
    this.resetCallTracking();
  }

  async hangup(): Promise<void> {
    const session = this.currentSession;
    if (!session) return;

    try {
      if (session instanceof Inviter) {
        switch (session.state) {
          case SessionState.Initial:
          case SessionState.Establishing:
            await session.cancel();
            break;
          case SessionState.Established:
            await session.bye();
            break;
          default:
            break;
        }
      } else if (session instanceof Invitation) {
        switch (session.state) {
          case SessionState.Initial:
          case SessionState.Establishing:
            await session.reject();
            break;
          case SessionState.Established:
            await session.bye();
            break;
          default:
            break;
        }
      }
    } catch {
      /* ignore */
    }

    this.currentSession = null;
  }

  async dial(rawNumber: string): Promise<void> {
    if (!this.userAgent || !this.creds?.domain) {
      throw new Error("SIP not registered");
    }

    const number = rawNumber.trim();
    if (!number) throw new Error("Empty dial number");

    const target = UserAgent.makeURI(`sip:${number}@${this.creds.domain}`);
    if (!target) throw new Error("Invalid dial target");

    const inviter = new Inviter(this.userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });

    this.currentSession = inviter;

    const sipCallId = createOutgoingCallId();
    const exten = this.extension();

    this.beginCallTracking({
      callId: sipCallId,
      caller: exten,
      destination: number,
      direction: "Outbound",
    });

    if (this.isWebhookBridgeEnabled()) {
      void this.postWebhook(
        buildStartWebhook({
          caller: exten,
          callee: number,
          uniqueid: sipCallId,
          startTime: this.callStartTimeStr!,
          useDevMocks: this.useDevMocks(),
          callDirection: "Outbound",
        })
      );
    }

    inviter.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this.markCallAnswered();
        this.attachRemote(inviter);

        if (this.sipCallId && this.isWebhookBridgeEnabled()) {
          void this.postWebhook(
            buildAnswerWebhook({
              caller: exten,
              callee: number,
              uniqueid: this.sipCallId,
              exten,
              answerTime: this.callAnswerTimeStr!,
              callDirection: "Outbound",
            })
          );
        }
      }

      if (state === SessionState.Terminated) {
        this.postEnd(
          this.callAnswered ? "Answer" : "Not Answer",
          "Normal call clearing"
        );
        this.currentSession = null;
        this.notifySessionEnded();
      }
    });

    await inviter.invite();
  }

  private async doConnect(): Promise<boolean> {
    this.setStatus("connecting");

    const creds = await this.fetchSipCredentials();
    if (!creds?.username || !creds.password || !creds.wsServer || !creds.sipUri) {
      // No extension provisioned at all is the normal state for most therapists —
      // report it separately so the UI doesn't warn them about a phone they never had.
      this.setStatus(this.credentialsConfigured ? "error" : "unconfigured");
      return false;
    }

    this.creds = creds;

    try {
      await this.registerSip(creds);
      this.setStatus("registered");
      return true;
    } catch {
      // Never report "error" over the SIP stack's own verdict — a rejected promise here
      // does not mean the registrar refused us.
      const registered = this.isSipRegistered();
      this.setStatus(registered ? "registered" : "error");
      return registered;
    }
  }

  private async fetchSipCredentials(): Promise<TSipCredentials | null> {
    if (!this.appDispatch) {
      this.credentialsConfigured = true;
      return null;
    }

    try {
      const creds = await this.appDispatch(
        paycallApi.endpoints.getPaycallSipCredentials.initiate(undefined, {
          subscribe: false,
          forceRefetch: true,
        })
      ).unwrap();
      this.credentialsConfigured = true;
      return creds;
    } catch (e) {
      // GET /Paycall/sip-credentials answers 404 when the user has no AgentSipCredentials
      // row. That is not a fault — they are just not a call-center agent. Any other
      // failure (network, 5xx, malformed row) IS worth surfacing.
      this.credentialsConfigured = (e as { status?: number })?.status !== 404;
      return null;
    }
  }

  private async registerSip(creds: TSipCredentials): Promise<void> {
    if (this.userAgent) {
      try {
        await this.userAgent.stop();
      } catch {
        /* ignore */
      }
    }

    const uri = UserAgent.makeURI(creds.sipUri);
    if (!uri) throw new Error("Invalid SIP URI");

    const service = this;
    this.userAgent = new UserAgent({
      uri,
      transportOptions: { server: creds.wsServer, keepAliveInterval: 30 },
      authorizationUsername: creds.username,
      authorizationPassword: creds.password,
      displayName: creds.displayName || creds.username,
      instanceId: getStableSipInstanceId(),
      logLevel: "warn",
      delegate: {
        onConnect() {
          service.registerer?.register().catch(() => {
            /* retry on next connect */
          });
        },
        onDisconnect(error) {
          if (error) {
            setTimeout(() => {
              service.userAgent?.reconnect().catch(() => {
                /* retry on next disconnect */
              });
            }, 3000);
          }
        },
        onInvite(invitation: Invitation) {
          service.handleInvite(invitation);
        },
      },
    });

    await this.userAgent.start();
    // regId + the UserAgent's stable instanceId emit reg-id/+sip.instance, so the
    // registrar replaces this endpoint's binding instead of stacking new contacts.
    this.registerer = new Registerer(this.userAgent, { regId: 1 });

    // Catches registration lost LATER — e.g. an admin changes the extension's
    // password and the periodic re-register (or the onConnect one above, which
    // passes no delegate) starts getting rejected.
    this.registerer.stateChange.addListener((state) => {
      if (state === RegistererState.Registered) {
        this.setStatus("registered");
        return;
      }
      if (
        this.status === "registered" &&
        (state === RegistererState.Unregistered ||
          state === RegistererState.Terminated)
      ) {
        this.setStatus("error");
      }
    });

    await this.awaitRegistration(this.registerer);
  }

  /**
   * Resolves only once the registrar ACCEPTS the REGISTER.
   *
   * `Registerer.register()` resolves as soon as the request is SENT, so awaiting it
   * alone reports success even when the credentials are wrong — the rejection arrives
   * asynchronously. Without this the UI would think it can receive calls while the
   * provider never routes an INVITE to this browser.
   */
  private awaitRegistration(registerer: Registerer): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;

      const onState = (state: RegistererState) => {
        if (state === RegistererState.Registered) settle();
      };

      const settle = (error?: Error) => {
        if (settled) return;
        settled = true;
        if (timer !== undefined) clearTimeout(timer);
        registerer.stateChange.removeListener(onState);
        if (error) reject(error);
        else resolve();
      };

      // The registrar's verdict can reach us two ways: through our own request delegate,
      // or through the state machine when a CONCURRENT register() — the one the transport
      // fires from onConnect — is the request that actually gets answered. Watch both, or
      // a harmless race makes us declare failure on a perfectly good registration.
      registerer.stateChange.addListener(onState);
      if (registerer.state === RegistererState.Registered) {
        settle();
        return;
      }

      timer = setTimeout(
        () => settle(new Error("SIP registration timed out")),
        SIP_REGISTRATION_TIMEOUT_MS
      );

      registerer
        .register({
          requestDelegate: {
            onAccept: () => settle(),
            onReject: (response) =>
              settle(
                new Error(
                  `SIP registration rejected (${response.message.statusCode})`
                )
              ),
          },
        })
        // A rejection here is NOT a verdict — the common one is RequestPendingError
        // ("REGISTER already in progress"). Let onState or the timeout decide.
        .catch(() => undefined);
    });
  }

  private handleInvite(invitation: Invitation): void {
    this.currentSession = invitation;

    const remoteUri = invitation.remoteIdentity?.uri;
    const caller = remoteUri?.user ?? "";
    const callId =
      invitation.request?.callId ?? `sip-${nowPaycallTimestamp()}`;

    this.beginCallTracking({
      callId,
      caller,
      direction: "Inbound",
    });

    if (this.isWebhookBridgeEnabled()) {
      void this.postWebhook(
        buildStartWebhook({
          caller,
          callee: PAYCALL_INBOUND_CALLEE,
          uniqueid: callId,
          startTime: this.callStartTimeStr!,
          useDevMocks: this.useDevMocks(),
          callDirection: "Inbound",
        })
      );
    }

    invitation.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this.attachRemote(invitation);
      }

      if (state === SessionState.Terminated) {
        this.postEnd(
          this.callAnswered ? "Answer" : "Not Answer",
          "Normal call clearing"
        );
        this.currentSession = null;
        this.notifySessionEnded();
      }
    });
  }

  private attachRemote(session: Session): void {
    if (!this.remoteAudio) return;

    try {
      const sdh = session.sessionDescriptionHandler;
      if (!sdh || !("peerConnection" in sdh)) return;

      const pc = (sdh as { peerConnection: RTCPeerConnection }).peerConnection;
      const stream = new MediaStream();

      pc.getReceivers().forEach((receiver) => {
        if (receiver.track) stream.addTrack(receiver.track);
      });

      this.remoteAudio.srcObject = stream;
      void this.remoteAudio.play().catch(() => {
        /* autoplay policy */
      });
    } catch {
      /* ignore */
    }
  }

  private async postWebhook(payload: TPaycallWebhookPayload): Promise<void> {
    if (!this.isWebhookBridgeEnabled() || !this.appDispatch) return;

    try {
      await this.appDispatch(
        paycallApi.endpoints.postPaycallWebhook.initiate(payload)
      ).unwrap();
    } catch {
      /* dev bridge is best-effort */
    }
  }

  private postEnd(disposition: string, hangupReason: string): void {
    if (!this.isWebhookBridgeEnabled() || this.endPosted || !this.sipCallId) {
      return;
    }

    this.endPosted = true;

    const endTime = nowPaycallTimestamp();
    const totalSec = this.callRingMs
      ? Math.max(0, Math.floor((Date.now() - this.callRingMs) / 1000))
      : 0;
    const answerSec =
      this.callAnswered && this.callAnswerMs
        ? Math.max(0, Math.floor((Date.now() - this.callAnswerMs) / 1000))
        : 0;
    const exten = this.extension();
    const callee =
      this.isOutbound && this.callDestination
        ? this.callDestination
        : PAYCALL_INBOUND_CALLEE;

    void this.postWebhook(
      buildEndWebhook({
        caller: this.sipCaller ?? "",
        callee,
        uniqueid: this.sipCallId,
        exten,
        disposition,
        hangupReason,
        startTime: this.callStartTimeStr || endTime,
        answerTime: this.callAnswerTimeStr || endTime,
        endTime,
        totalSec,
        answerSec,
        did: this.callDid ?? "",
        useDevMocks: this.useDevMocks(),
        callDirection: this.callDirection,
      })
    );

    this.resetCallTracking();
  }

  private beginCallTracking(params: {
    callId: string;
    caller: string;
    direction: TCallDirection;
    destination?: string;
  }): void {
    this.callAnswered = false;
    this.endPosted = false;
    this.callStartTimeStr = nowPaycallTimestamp();
    this.callAnswerTimeStr = null;
    this.callRingMs = Date.now();
    this.callAnswerMs = null;
    this.callDid = this.useDevMocks() ? PAYCALL_DEV_MOCK.did : "";
    this.sipCaller = params.caller;
    this.sipCallId = params.callId;
    this.callDestination = params.destination ?? null;
    this.callDirection = params.direction;
    this.isOutbound = params.direction === "Outbound";
    if (this.isOutbound) {
      this.notifyRemoteAnswered(false);
    }
  }

  private markCallAnswered(): void {
    if (this.callAnswered) return;
    this.callAnswered = true;
    this.callAnswerTimeStr = nowPaycallTimestamp();
    this.callAnswerMs = Date.now();
    if (this.isOutbound) {
      this.notifyRemoteAnswered(true);
    }
  }

  private notifyRemoteAnswered(answered: boolean): void {
    this.remoteAnsweredListeners.forEach((cb) => cb(answered));
  }

  private resetCallTracking(): void {
    this.sipCallId = null;
    this.sipCaller = null;
    this.callDestination = null;
    this.callAnswered = false;
    this.endPosted = false;
    this.callStartTimeStr = null;
    this.callAnswerTimeStr = null;
    this.callRingMs = null;
    this.callAnswerMs = null;
    this.callDid = null;
    this.callDirection = "Inbound";
    this.isOutbound = false;
    this.notifyRemoteAnswered(false);
  }

  private extension(): string {
    return this.creds?.username?.trim() ?? "";
  }

  private isWebhookBridgeEnabled(): boolean {
    return isPaycallSipWebhookBridgeEnabled();
  }

  private useDevMocks(): boolean {
    return paycallDevMockFieldsEnabled();
  }

  private setStatus(next: TSipConnectionStatus): void {
    this.status = next;
    this.statusListeners.forEach((cb) => cb(next));
  }

  private notifySessionEnded(): void {
    this.sessionEndedListeners.forEach((cb) => cb());
  }
}

export const paycallSoftphone = new PaycallSoftphoneService();
